import React, { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import axios from "axios";

//*MUI
import { Box, Modal } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

//*files
import cssClasses from "./AgreementDoneModal.module.css";
import Close from "../../../../Components/Icons/Close";
import Button from "../../../../Subcomponents/Button";
import CustomInput from "../../../../Subcomponents/CustomInput";
import DesktopDatePickerComponent from "../../../../Subcomponents/DesktopDatePicker";
import { errorTost, successTost } from "../../../../Services/Toast";
import { dismissLoader, presentLoader } from "../../../../Services/loader";
import { uploadAgreementReceipt } from "../../../../Services/AllAllotments";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  height: "fit-Content",
  maxHeight: "90%",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

// Accepted document/image formats for receipt and index uploads
const ACCEPTED_FILES =
  "image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const INITIAL_FORM = {
  agreementDate: "",
  registrationNumber: "",
  officeName: "",
  receiptFile: null,
};

const INITIAL_HELPER = {
  agreementDate: "",
  registrationNumber: "",
  officeName: "",
  receiptFile: "",
};

function AgreementDoneModal({
  open,
  handleClose,
  allotment,
  getCrmAllotmentsFromServer,
}) {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const [agreementForm, setAgreementForm] = useState(INITIAL_FORM);
  const [helperText, setHelperText] = useState(INITIAL_HELPER);

  const isFormValid =
    agreementForm.agreementDate &&
    agreementForm.registrationNumber?.trim() &&
    agreementForm.officeName?.trim() &&
    agreementForm.receiptFile;

  const updateField = (field, value) => {
    setAgreementForm((pre) => ({ ...pre, [field]: value }));
    setHelperText((pre) => ({ ...pre, [field]: "" }));
  };

  const validate = () => {
    const nextHelper = { ...INITIAL_HELPER };
    if (!agreementForm.agreementDate)
      nextHelper.agreementDate = "Please select agreement date.";
    if (!agreementForm.registrationNumber?.trim())
      nextHelper.registrationNumber = "Please enter registration number.";
    if (!agreementForm.officeName?.trim())
      nextHelper.officeName = "Please enter office name.";
    if (!agreementForm.receiptFile)
      nextHelper.receiptFile = "Please upload the receipt.";
    setHelperText(nextHelper);
    return Object.values(nextHelper).every((msg) => msg === "");
  };

  const closeAndReset = () => {
    setAgreementForm(INITIAL_FORM);
    setHelperText(INITIAL_HELPER);
    handleClose();
  };

  const handleAgreementDone = async () => {
    if (!validate()) {
      errorTost("Please fill all the required fields.");
      return;
    }

    const allotmentPaymentId = allotment?.allotment_payment_id;
    try {
      presentLoader();

      // 1. Upload the receipt to the AgreementKYCDocuments bucket -> public URL.
      const receiptUrl = await uploadAgreementReceipt(
        agreementForm.receiptFile,
        allotmentPaymentId,
      );

      // 2. Call the backend endpoint that owns the full agreement-done flow
      //    (writes AgreementForm.agreementStatus, flips AllotmentPayment.status,
      //    moves the CRM tab, updates lead/inventory and all logs). We only send
      //    the payload; the backend handles the rest. registration_date mirrors
      //    agreement_date and index_url is null for the manual path.
      const agreementDate = dayjs(agreementForm.agreementDate).format(
        "DD MMMM YYYY",
      );
      const payload = {
        bookingId: allotmentPaymentId,
        agreementDoneStatus: true,
        agreementDate,
        registration_no: agreementForm.registrationNumber?.trim(),
        registration_date: agreementDate,
        index_url: null,
        office_name: agreementForm.officeName?.trim(),
        receipt_url: receiptUrl,
        userName: loggedInUser?.name,
        userId: loggedInUser?.id,
      };

      const response = await axios.post(
        process.env.REACT_APP_PAYMENT_LINK_URL +
          "agreement/updateAgreementStatus",
        payload,
      );

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
            response?.data?.error?.message ||
            "Agreement status update failed.",
        );
      }

      dismissLoader();
      successTost("Agreement details saved successfully.");
      getCrmAllotmentsFromServer?.(false);
      closeAndReset();
    } catch (error) {
      dismissLoader();
      console.error("Agreement Done error =>", error);
      errorTost(error?.message || "Something went wrong! Try again.");
    }
  };

  const renderUnit = () => {
    if (allotment?.inventory_merge_id) {
      return `${
        allotment?.mergeddisplaywingname || allotment?.merged_wing_name
      } - ${allotment?.merged_unit_no_one} - ${allotment?.merged_unit_no_two}`;
    }
    return `${allotment?.displaywingname || allotment?.wing_name} - ${
      allotment?.unit_no
    }`;
  };

  const renderProject = () =>
    allotment?.inventory_merge_id
      ? allotment?.merged_project_name
      : allotment?.project_name;

  const renderUploadBox = (field, inputId, isMandatory) => {
    const file = agreementForm[field];
    const hasError = field === "receiptFile" && helperText.receiptFile !== "";
    return (
      <>
        <Box
          className={`${cssClasses.uploadBox} ${
            hasError ? cssClasses.uploadBoxError : ""
          }`}
          onClick={() => document.getElementById(inputId).click()}
        >
          {file ? (
            <div className={cssClasses.fileRow}>
              <span className={cssClasses.fileName}>{file.name}</span>
              <span
                className={cssClasses.removeFile}
                onClick={(e) => {
                  e.stopPropagation();
                  updateField(field, null);
                }}
              >
                Remove
              </span>
            </div>
          ) : (
            <>
              <div className={cssClasses.uploadImg}>
                <FileUploadOutlinedIcon
                  style={{ fontSize: "2.4rem", color: "#475467" }}
                />
              </div>
              <div className={cssClasses.uploadLabel}>Upload here</div>
              <div className={cssClasses.uploadHint}>
                Image, PDF or Word document
              </div>
            </>
          )}
          <input
            hidden
            type="file"
            id={inputId}
            accept={ACCEPTED_FILES}
            onChange={(e) => {
              if (e.target.files?.[0]) {
                updateField(field, e.target.files[0]);
              }
              e.target.value = "";
            }}
          />
        </Box>
        {isMandatory && hasError && (
          <div className={cssClasses.helperText}>{helperText.receiptFile}</div>
        )}
      </>
    );
  };

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          closeAndReset();
        }
      }}
      aria-labelledby="agreement-done-modal-title"
    >
      <Box sx={style}>
        {/* Header */}
        <Box className={cssClasses.headerDiv}>
          <Box className={cssClasses.flexRow} style={{ alignItems: "center" }}>
            <Box className={cssClasses.HeaderTitle}>Agreement</Box>
            <Box
              ml={"auto"}
              onClick={closeAndReset}
              style={{ cursor: "pointer" }}
            >
              <Close width={"1.2rem"} height={"1.2rem"} />
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box className={cssClasses.bodyDiv} style={{ overflowY: "auto" }}>
          {/* Context */}
          <Box className={cssClasses.leadDetailsRow}>
            <div className={cssClasses.leadDetails}>
              <p>Customer Name:</p>
              <div>{allotment?.lead_name || "-"}</div>
            </div>
            <div className={cssClasses.leadDetails}>
              <p>Unit:</p>
              <div>
                {renderUnit()} | {renderProject()}
              </div>
            </div>
          </Box>

          {/* Fields */}
          <Box className={cssClasses.fieldGrid}>
            <Box>
              <div className={cssClasses.InputTitle}>
                Agreement Date<span className={cssClasses.Star}> *</span>
              </div>
              <DesktopDatePickerComponent
                value={agreementForm.agreementDate}
                minDate={new Date("2000-01-01")}
                maxDate={new Date()}
                onChangeDate={(value) => updateField("agreementDate", value)}
                helperText={helperText.agreementDate}
              />
            </Box>

            <Box>
              <div className={cssClasses.InputTitle}>
                Registration Number<span className={cssClasses.Star}> *</span>
              </div>
              <CustomInput
                placeholder={"Enter registration number"}
                value={agreementForm.registrationNumber}
                handleChange={(newValue) =>
                  updateField("registrationNumber", newValue)
                }
                helperText={helperText.registrationNumber}
              />
            </Box>

            <Box>
              <div className={cssClasses.InputTitle}>
                Office Name<span className={cssClasses.Star}> *</span>
              </div>
              <CustomInput
                placeholder={"Enter office name"}
                value={agreementForm.officeName}
                handleChange={(newValue) => updateField("officeName", newValue)}
                helperText={helperText.officeName}
              />
            </Box>

            <Box className={cssClasses.fieldFull}>
              <div className={cssClasses.InputTitle}>
                Receipt Upload<span className={cssClasses.Star}> *</span>
              </div>
              {renderUploadBox("receiptFile", "agreementReceiptUpload", true)}
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box className={cssClasses.FooterDiv}>
          <Box
            className={cssClasses.flexRow}
            style={{ justifyContent: "flex-end" }}
          >
            <Button
              style={{
                fontSize: "1.4rem",
                lineHeight: "2rem",
                color: "#344054",
                backgroundColor: "#FFFFFF",
                borderRadius: "0.8rem",
                border: "1px solid #D0D5DD",
              }}
              padding={"0.85rem 2.6rem"}
              btnName={"Cancel"}
              clicked={closeAndReset}
            />
            <Button
              style={{
                fontSize: "1.4rem",
                lineHeight: "2rem",
                color: "#FFFFFF",
                backgroundColor: "#006AFF",
                borderRadius: "0.8rem",
              }}
              padding={"0.85rem 3.2rem"}
              btnName={"Agreement Done"}
              disable={!isFormValid}
              clicked={handleAgreementDone}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default AgreementDoneModal;
